/** Shared helpers of the Portfolio page and its dashboard widgets (lot L4, plan §7.3). */
import i18n from '../../i18n/config';
import type { PortfolioMapPoint } from '../../types/portfolio';

/** Map palette (legend and map share it). */
export const MAP_COLORS = {
  /** Dashed motif outlines and domain names. */
  motif: '#3B82F6',
  /** Qualified projects. */
  qualified: '#16A34A',
  /** Members of a recommended rapprochement. */
  suggestion: '#F97316',
} as const;

const locale = (): string => (i18n.language?.startsWith('en') ? 'en-GB' : 'fr-FR');

/** "octobre 2026" */
export const monthLabel = (d: Date): string => d.toLocaleDateString(locale(), { month: 'long', year: 'numeric' });

/** "octobre" */
export const monthName = (d: Date): string => d.toLocaleDateString(locale(), { month: 'long' });

export const monthStart = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), 1);
export const nextMonth = (d: Date): Date => new Date(d.getFullYear(), d.getMonth() + 1, 1);

/** Months of the timeline: at least the last twelve, extended back to the first deposit. */
export function buildMonths(points: readonly PortfolioMapPoint[]): Date[] {
  const end = monthStart(new Date());
  let start = new Date(end.getFullYear(), end.getMonth() - 11, 1);
  for (const p of points) {
    const m = monthStart(new Date(p.deposited_at));
    if (!Number.isNaN(m.getTime()) && m < start) start = m;
  }
  const out: Date[] = [];
  for (let d = start; d <= end; d = nextMonth(d)) out.push(d);
  return out;
}

export function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}

/** Hex colour → rgba() with the given alpha (KPI tile tints). */
export function tint(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/** A readable point (one the caller may open). */
export type ReadablePoint = PortfolioMapPoint & { id: string };
export const isReadable = (p: PortfolioMapPoint): p is ReadablePoint => p.id !== null;
